import {
    createApi,
    fetchBaseQuery,
    FetchBaseQueryError,
} from "@reduxjs/toolkit/query/react";
import Config, { apiBase } from "../constants/Config";
import { TypeOf, z } from "zod";
import { getUserNonce } from "../api";

const RelatedTicket = z.object({
    id: z.number().positive().int(),
    acf: z.intersection(
        z.object({
            ticket_available_from: z.string(),
            ticket_available_until: z.string(),
            product_behaviour: z.string(),
            team_size: z
                .string()
                .transform((val, ctx) => {
                    if (val === "") {
                        return undefined;
                    }
                    const parsed = Number.parseInt(val, 10);
                    if (isNaN(parsed)) {
                        ctx.addIssue({
                            code: z.ZodIssueCode.custom,
                            message: "Not a number",
                        });

                        return z.NEVER;
                    }
                    return parsed;
                })
                .optional(),
        }),
        z.union([
            z.object({
                eligible_for: z.literal("club_member"),
                eligible_club: z.number().positive().int(),
            }),
            z.object({
                eligible_for: z.union([
                    z.literal("public"),
                    z.literal("alumni"),
                    z.literal("college"),
                    z.literal("staff"),
                    z.literal("student"),
                    z.literal("non_club_member"),
                ]),
                eligible_club: z.literal(false),
            }),
        ])
    ),
    price: z.string(),
    stock_status: z.string(),
    stock_quantity: z.number().nonnegative().int(),
    meta_data: z.array(
        z.object({
            key: z.string(),
            value: z.string(),
        })
    ),
    type: z.string(),
});

const RelatedDto = z.union([RelatedTicket, z.object({}), z.literal(false)]);

export const PageDto = z
    .object({
        ID: z.number().positive().int(),
        acf: z.object({
            event_products: z.array(
                z.object({
                    product: z.object({ ID: z.number() }),
                })
            ),
            event_ticket_comment_label: z.string().optional(),
            ticket_additional_questions: z.array(z.string()).optional(),
        }),
        related: z.record(RelatedDto),
    })
    .transform((value, ctx) => {
        const isTicket = (
            relatedItem: TypeOf<typeof RelatedDto>
        ): relatedItem is TypeOf<typeof RelatedTicket> => {
            return (relatedItem as TypeOf<typeof RelatedTicket>)?.id != null;
        };
        const relatedTickets = Object.values(value.related).filter(isTicket);

        return {
            ...value,
            acf: {
                ...value.acf,
                event_products: value.acf.event_products.map(eventProduct => {
                    const relatedData = relatedTickets.find(
                        ticket => ticket.id === eventProduct.product.ID
                    );
                    if (relatedData == null) {
                        ctx.addIssue({
                            code: z.ZodIssueCode.custom,
                            message: `Missing related data for event_product(${eventProduct.product.ID})`,
                        });
                        return z.NEVER;
                    }
                    return {
                        ...eventProduct,
                        ...relatedData,
                    };
                }),
            },
        };
    });

export const GrantDto = z.array(
    z.object({
        created_on: z.string().nullish(),
        grant_type: z.string().nullish(),
        status: z.string().nullish(),
        total_value_requested: z.string().nullish(),
        total_value_allocated: z.string().nullish(),
        name: z.string().nullish(),
    })
);

export const SponsorshipDto = z.array(
    z.object({
        created_on: z.string().nullish(),
        sponsorship_type: z.string().nullish(),
        sponsorship_end_date: z.string().nullish(),
        amount: z.string().nullish(),
        company_name: z.string().nullish(),
        status: z.string().nullish(),
    })
);

export const GovernanceDto = z.array(
    z.object({
        name: z.string().nullish(),
        created_on: z.string().nullish(),
        date_of_meeting: z.string().nullish(),
        status: z.string().nullish(),
    })
);

export const EventApplicationDto = z.array(
    z.object({
        created_on: z.string().nullish(),
        status: z.string().nullish(),
        name: z.string().nullish(),
        event_date: z.string().nullish(),
        location: z.string().nullish(),
    })
);

export const wp = createApi({
    reducerPath: "wp",
    baseQuery: fetchBaseQuery({
        baseUrl: `${apiBase}/`,
        fetchFn: async (input, init) => {
            const url =
                typeof input === "string" ? new URL(input) : new URL(input.url);

            const matchablePath = url.pathname.slice(1);

            if (
                matchablePath.startsWith(
                    `wp-json/${Config.dynamicPageAPISubpath}`
                ) ||
                matchablePath.startsWith(`wp-json/${Config.shopAPISubpath}`) ||
                matchablePath.startsWith(`wp-json/${Config.userAPISubpath}`)
            ) {
                const nonceResponse = await getUserNonce();
                typeof input !== "string" &&
                    input.headers.set("X-WP-Nonce", nonceResponse.nonce);
            }

            return fetch(input, init);
        },
    }),
    endpoints: builder => ({
        getPageByPath: builder.query<
            TypeOf<typeof PageDto>,
            | { preview?: false; path: string }
            | { preview: true; page_id: string }
        >({
            queryFn: async (params, _queryApi, extraOptions, baseQuery) => {
                let result;
                if (params.preview) {
                    result = await baseQuery(
                        `${Config.dynamicPageAPISubpath}?preview=true&page_id=${params.page_id}`
                    );
                } else {
                    if (params.path.startsWith("/product/")) {
                        result = await baseQuery(
                            `${Config.pageAPISubpath}?path=${params.path}`
                        );
                    } else {
                        const pageResponse = await baseQuery(
                            `${Config.pageAPISubpath}?path=${params.path}`
                        );

                        if (pageResponse.data) {
                            const pageData = z
                                .record(z.any())
                                .parse(pageResponse.data);
                            if (pageData.post_status === "private") {
                                result = await baseQuery(
                                    `${Config.dynamicPageAPISubpath}?path=${params.path}`
                                );
                            } else {
                                result = pageResponse;
                            }
                        } else {
                            result = pageResponse;
                        }
                    }
                }

                return result.data
                    ? {
                          data: PageDto.parse(result.data),
                      }
                    : { error: result.error as FetchBaseQueryError };
            },
        }),
        getGrantsByClub: builder.query({
            query: (clubId: number) =>
                `user/v1/my-managed-clubs/${clubId}/grants`,
            transformResponse: input => GrantDto.parse(input),
        }),
        getSponsorshipsByClub: builder.query({
            query: (clubId: number) =>
                `user/v1/my-managed-clubs/${clubId}/sponsorships`,
            transformResponse: input => SponsorshipDto.parse(input),
        }),
        getGovernancesByClub: builder.query({
            query: (clubId: number) =>
                `user/v1/my-managed-clubs/${clubId}/governances`,
            transformResponse: input => GovernanceDto.parse(input),
        }),
        getEventApplicationsByClub: builder.query({
            query: (clubId: number) =>
                `user/v1/my-managed-clubs/${clubId}/event-applications`,
            transformResponse: input => EventApplicationDto.parse(input),
        }),
    }),
});

export const {
    useGetPageByPathQuery,
    useGetGrantsByClubQuery,
    useGetSponsorshipsByClubQuery,
    useGetGovernancesByClubQuery,
    useGetEventApplicationsByClubQuery,
} = wp;
